// Timeline della pagina "Su Martin" (template-parts/su-martin/timeline.php):
// stesso motore di avanzamento automatico di assets/js/timeline.js (home),
// adattato a un "quadrante" verticale che mostra solo l'anno attivo e i due
// adiacenti (gli altri sfumano via CSS in base a [data-distance], vedi
// assets/scss/_su-martin.scss) invece della riga orizzontale con tutti gli
// anni della home. Su mobile/tablet il quadrante diventa una riga
// orizzontale (vedi la media query in _su-martin.scss): lo stesso codice
// funziona in entrambi i casi misurando l'asse (verticale/orizzontale) dal
// flex-direction calcolato del "tamburo" invece di averlo cablato.
function initSuMartinTimelineSection(section) {
    const years = Array.from(section.querySelectorAll(".su-martin-timeline__year"));
    const panels = Array.from(section.querySelectorAll(".su-martin-timeline__panel"));
    const dial = section.querySelector(".su-martin-timeline__dial");
    const drum = section.querySelector("[data-su-martin-drum]");
    const fill = section.querySelector("[data-su-martin-fill]");
    const pauseButton = section.querySelector("[data-su-martin-pause]");

    if (!years.length || !panels.length || !dial || !drum) {
        return;
    }

    const interval = parseInt(section.dataset.interval, 10) || 5000;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let activeIndex = 0;
    let timer = null;
    let isPaused = reducedMotion;
    // Stesso principio di assets/js/timeline.js: durata rimasta nel ciclo
    // corrente e istante in cui è ripartito, per permettere a pause()/play()
    // di fermare la barra esattamente dov'è e farla ripartire dallo stesso
    // punto invece di riavviare un intervallo pieno.
    let cycleDuration = interval;
    let cycleStartedAt = 0;

    // Legge in tempo reale se il tamburo scorre in verticale (quadrante
    // desktop) o in orizzontale (riga mobile, vedi la media query in
    // _su-martin.scss): la stessa funzione di centratura funziona in
    // entrambi i casi leggendo le proprietà giuste per l'asse corrente.
    function axis() {
        const isRow = window.getComputedStyle(drum).flexDirection.indexOf("row") === 0;

        return isRow
            ? { offset: "offsetLeft", size: "offsetWidth", transform: (v) => `translateX(${v}px)` }
            : { offset: "offsetTop", size: "offsetHeight", transform: (v) => `translateY(${v}px)` };
    }

    // Centra l'anno attivo nel quadrante: il tamburo è ancorato al centro del
    // proprio contenitore (CSS), quindi basta traslarlo dell'opposto della
    // posizione del centro del bottone attivo rispetto al tamburo stesso.
    function centerDrum(animate) {
        const button = years[activeIndex];

        if (!button) {
            return;
        }

        const { offset, size, transform } = axis();
        const center = button[offset] + button[size] / 2;

        drum.style.transition = animate && !reducedMotion ? "transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)" : "none";
        drum.style.transform = transform(-center);
    }

    // Ferma la barra alla percentuale corrispondente all'anno attivo, senza transizione.
    function placeFillAt(index, animate) {
        if (!fill || years.length < 2) {
            return;
        }

        const percent = (index / (years.length - 1)) * 100;

        fill.style.transition = animate && !reducedMotion ? "height 0.4s ease" : "none";
        fill.style.height = `${percent}%`;
        void fill.offsetHeight;
    }

    // Fa "caricare" la barra in tempo reale verso la percentuale del prossimo
    // anno per tutta la durata passata (intervallo pieno, o tempo rimasto
    // dopo una pausa — vedi resumeCycle): stesso principio di
    // assets/js/timeline.js, qui in altezza invece che in larghezza.
    function animateFillToNext(duration) {
        if (!fill || years.length < 2) {
            return;
        }

        const nextIndex = (activeIndex + 1) % years.length;
        const percent = (nextIndex / (years.length - 1)) * 100;

        fill.style.transition = `height ${duration}ms linear`;
        fill.style.height = `${percent}%`;
    }

    // Blocca la barra all'altezza renderizzata in questo istante, interrompendo
    // la transizione CSS in corso senza farla scattare al valore finale.
    function freezeFill() {
        if (!fill) {
            return;
        }

        const currentHeight = fill.getBoundingClientRect().height;
        const railHeight = fill.parentElement.getBoundingClientRect().height;
        const percent = railHeight > 0 ? (currentHeight / railHeight) * 100 : 0;

        fill.style.transition = "none";
        fill.style.height = `${percent}%`;
        void fill.offsetHeight;
    }

    function goTo(index) {
        activeIndex = (index + years.length) % years.length;

        years.forEach((year, i) => {
            // Distanza lineare (non circolare): il tamburo è disposto in
            // ordine cronologico, non a "giostra", quindi il primo e
            // l'ultimo anno non devono considerarsi adiacenti solo perché
            // vicini nell'array che si ripete con l'autoplay.
            const distance = Math.abs(i - activeIndex);
            const isActive = i === activeIndex;

            year.classList.toggle("is-active", isActive);
            year.dataset.distance = String(distance);
            year.setAttribute("aria-selected", isActive ? "true" : "false");
            // Roving tabindex (pattern ARIA tablist): un solo tab è raggiungibile
            // con Tab, gli altri si raggiungono con le frecce (vedi keydown sotto).
            year.setAttribute("tabindex", isActive ? "0" : "-1");
        });

        panels.forEach((panel, i) => panel.classList.toggle("is-active", i === activeIndex));

        centerDrum(true);
        startCycle(interval);
    }

    function stopTimer() {
        if (timer) {
            window.clearTimeout(timer);
            timer = null;
        }
    }

    // Avvia un ciclo di `duration` ms per l'anno attivo: posiziona la barra
    // sulla percentuale corrente e, se non in pausa/reduced-motion, la fa
    // avanzare verso il prossimo anno e pianifica il cambio allo scadere del tempo.
    function startCycle(duration) {
        stopTimer();
        cycleDuration = duration;
        placeFillAt(activeIndex, false);

        if (isPaused || reducedMotion) {
            return;
        }

        animateFillToNext(duration);
        cycleStartedAt = Date.now();
        timer = window.setTimeout(() => goTo(activeIndex + 1), duration);
    }

    // Riprende il ciclo interrotto da pause() per il tempo rimasto, mantenendo
    // la stessa velocità di avanzamento (non un intervallo pieno da capo).
    function resumeCycle() {
        if (cycleDuration <= 0) {
            goTo(activeIndex + 1);
            return;
        }

        animateFillToNext(cycleDuration);
        cycleStartedAt = Date.now();
        timer = window.setTimeout(() => goTo(activeIndex + 1), cycleDuration);
    }

    // Controllo esplicito di pausa (WCAG 2.2.2): ferma la barra esattamente
    // dov'è e il cambio anno, senza far ripartire un intervallo pieno alla ripresa.
    function updatePauseButton() {
        if (!pauseButton) {
            return;
        }

        pauseButton.classList.toggle("is-paused", isPaused);
        pauseButton.setAttribute(
            "aria-label",
            isPaused ? "Riprendi la timeline" : "Metti in pausa la timeline"
        );
    }

    function pause() {
        if (isPaused) {
            return;
        }

        isPaused = true;
        cycleDuration = Math.max(cycleDuration - (Date.now() - cycleStartedAt), 0);
        freezeFill();
        stopTimer();
        updatePauseButton();
    }

    function play() {
        if (reducedMotion || !isPaused) {
            return;
        }

        isPaused = false;
        resumeCycle();
        updatePauseButton();
    }

    if (pauseButton) {
        pauseButton.addEventListener("click", () => {
            if (isPaused) {
                play();
            } else {
                pause();
            }
        });
    }

    // In pausa mentre il puntatore è sopra la sezione (comodo per leggere un
    // anno senza dover cliccare il bottone di pausa), ripristinata quando esce
    // — solo se l'utente non l'ha già messa in pausa esplicitamente.
    let hoverPaused = false;
    section.addEventListener("mouseenter", () => {
        if (!isPaused) {
            hoverPaused = true;
            pause();
        }
    });
    section.addEventListener("mouseleave", () => {
        if (hoverPaused) {
            hoverPaused = false;
            play();
        }
    });

    years.forEach((year, i) => {
        year.addEventListener("click", () => goTo(i));
    });

    // Frecce/Home/End per muoversi tra i tab (ARIA Authoring Practices,
    // pattern Tabs con attivazione automatica): funzionano sia in verticale
    // (Su/Giù) sia in orizzontale (Sinistra/Destra), utile perché il
    // quadrante cambia asse tra desktop e mobile.
    drum.addEventListener("keydown", (event) => {
        const { key } = event;

        if (key === "ArrowLeft" || key === "ArrowUp") {
            event.preventDefault();
            goTo(activeIndex - 1);
        } else if (key === "ArrowRight" || key === "ArrowDown") {
            event.preventDefault();
            goTo(activeIndex + 1);
        } else if (key === "Home") {
            event.preventDefault();
            goTo(0);
        } else if (key === "End") {
            event.preventDefault();
            goTo(years.length - 1);
        } else {
            return;
        }

        years[activeIndex].focus();
    });

    window.addEventListener("resize", () => centerDrum(false));

    goTo(0);
    updatePauseButton();
}

export function initSuMartinTimeline() {
    document.querySelectorAll("[data-su-martin-timeline]").forEach(initSuMartinTimelineSection);
}
